import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { AVAILABLE_COUNTRIES } from "@/constants/campaignBuilder";

interface Props {
  selectedCountry: string;
  onChange: (code: string) => void;
  /** Compact pill-half trigger (flag + chevron only). Used in FullscreenPreviewOverlay. */
  compact?: boolean;
  /** Override the selectable list (default: the full shipping allowlist, AVAILABLE_COUNTRIES).
   *  Callers generally leave this unset so the recipient can pick any allowlisted country. */
  countries?: { code: string; flag: string; name: string }[];
  /** Read-only: the country is fixed by the campaign (US-only, or a Pro+ recipient-list
   *  country). Shows a lock instead of the chevron and does not open the menu. Honored in
   *  both the compact and full (flag+name) triggers. */
  locked?: boolean;
  /** Full trigger stretches to its container instead of the default 160px min-width — for form
   *  rows (e.g. the Add Recipient modal) where it sits alongside full-width inputs. */
  fullWidth?: boolean;
  /** Portal menu z-index. The default (MUI 1300) sits BELOW a high-stacking overlay, so a caller
   *  inside one (the Add Recipient modal is z-10000) must lift the menu above it. */
  menuZIndex?: number;
}

const SEARCH_FIELD_SX = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    fontSize: "14px",
    borderRadius: "6px",
    "& fieldset": { borderColor: "var(--color-smoke)" },
    "&:hover fieldset": { borderColor: "var(--color-smoke)" },
    "&.Mui-focused fieldset": { borderColor: "var(--color-dark-dust)" },
  },
  "& .MuiInputBase-input": {
    color: "var(--color-graphite)",
    py: "6px",
    "&::placeholder": { opacity: 1 },
  },
  // R16: this field autofocuses on open; below 14px iOS zooms the whole page on focus.
  "@media (max-width: 767px)": {
    "& .MuiOutlinedInput-root": { fontSize: "16px" },
  },
};

const MENU_PAPER_SX = {
  border: "1px solid var(--color-dark-dust)",
  borderRadius: "10px",
  boxShadow: "0px 4px 6px -1px rgba(0,0,0,0.10), 0px 2px 4px -2px rgba(0,0,0,0.10)",
  mt: "4px",
  maxHeight: 320,
  "& .MuiList-root": { pt: 0 },
};

export default function CountryDropdown({
  selectedCountry,
  onChange,
  compact = false,
  countries = AVAILABLE_COUNTRIES,
  locked = false,
  fullWidth = false,
  menuZIndex,
}: Props) {
  const [searchTerm, setSearchTerm] = useState("");
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const menuOpen = Boolean(anchor);
  const selectedEntry = countries.find((c) => c.code === selectedCountry);

  const filtered = countries.filter((c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()));

  function handleClose() {
    setAnchor(null);
    setSearchTerm("");
  }

  function handleSelect(code: string) {
    onChange(code);
    handleClose();
  }

  const chevron = (
    <ExpandMoreIcon
      sx={{
        fontSize: 16,
        opacity: compact ? 0.85 : 1,
        transition: "transform 0.18s ease",
        transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
      }}
    />
  );

  return (
    <>
      {compact ? (
        // Compact: flag + chevron only — sits inside the segmented pill in
        // FullscreenPreviewOverlay + the redemption "Shipping to". When locked, the
        // country is fixed: show a lock, don't open the menu.
        <button
          type="button"
          onClick={locked ? undefined : (e) => setAnchor(e.currentTarget)}
          disabled={locked}
          aria-haspopup={locked ? undefined : "true"}
          aria-expanded={locked ? undefined : menuOpen}
          title={
            locked
              ? `Shipping to ${selectedEntry?.name ?? "the selected country"}`
              : "Preview by location"
          }
          className={
            locked
              ? "flex items-center gap-1 px-2.5 py-2 text-rodeo-black cursor-default"
              : "flex items-center gap-0.5 px-2.5 py-2 text-rodeo-black bg-transparent border-none cursor-pointer transition-colors hover:bg-light-dust"
          }
          style={{ borderRadius: "0 4px 4px 0" }}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }}>{selectedEntry?.flag ?? "🌐"}</span>
          {locked ? <LockOutlinedIcon sx={{ fontSize: 13, opacity: 0.45 }} /> : chevron}
        </button>
      ) : (
        // Full: flag + name — used in CollectionViewControls ("Available Gifts in") and the
        // redemption "Shipping to". When locked, the country is fixed: show a lock, no menu.
        <button
          type="button"
          onClick={locked ? undefined : (e) => setAnchor(e.currentTarget)}
          disabled={locked}
          aria-haspopup={locked ? undefined : "true"}
          aria-expanded={locked ? undefined : menuOpen}
          className={`inline-flex items-center gap-2 h-9.25 px-3 border rounded-sm bg-white font-lato text-[12px] text-rodeo-black ${
            fullWidth ? "w-full" : ""
          } ${locked ? "cursor-default" : "cursor-pointer transition-colors hover:border-dark-dust"}`}
          style={{ minWidth: fullWidth ? undefined : 160, borderColor: "var(--color-dark-dust)" }}
        >
          <span>{selectedEntry?.flag}</span>
          <span className="flex-1 text-left">{selectedEntry?.name ?? "Country"}</span>
          {locked ? <LockOutlinedIcon sx={{ fontSize: 14, opacity: 0.45 }} /> : chevron}
        </button>
      )}

      {/* Single Menu shared by both modes */}
      <Menu
        anchorEl={anchor}
        open={menuOpen}
        onClose={handleClose}
        sx={{ zIndex: menuZIndex ?? (compact ? 10003 : undefined) }}
        slotProps={{ paper: { sx: { ...MENU_PAPER_SX, width: 240 } } }}
        transformOrigin={
          compact
            ? { horizontal: "right", vertical: "top" }
            : { horizontal: "left", vertical: "top" }
        }
        anchorOrigin={
          compact
            ? { horizontal: "right", vertical: "bottom" }
            : { horizontal: "left", vertical: "bottom" }
        }
        autoFocus={false}
      >
        <Box sx={{ px: "12px", py: "8px", borderBottom: "1px solid var(--color-card-border)" }}>
          <TextField
            size="small"
            placeholder="Search Countries ..."
            value={searchTerm}
            autoFocus
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "var(--color-graphite)" }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={SEARCH_FIELD_SX}
          />
        </Box>

        {filtered.length === 0 ? (
          <MenuItem disabled>
            <Typography
              sx={{
                fontFamily: "var(--font-lato)",
                fontSize: "12px",
                color: "var(--color-graphite)",
              }}
            >
              No countries found
            </Typography>
          </MenuItem>
        ) : (
          filtered.map((c) => (
            <MenuItem
              key={c.code}
              selected={c.code === selectedCountry}
              onClick={() => handleSelect(c.code)}
              sx={{ minHeight: 34, py: "6px", px: "24px" }}
            >
              <span className="flex items-center gap-2">
                <span>{c.flag}</span>
                <Typography sx={{ fontSize: "12px", color: "var(--color-rodeo-black)" }}>
                  {c.name}
                </Typography>
              </span>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
